import React, { forwardRef, ForwardRefRenderFunction } from "react";
//#region types
import {
  AutoCompleteExtraProps,
  EditableTableFieldProps,
  FormFieldDefinitionType,
  FormHandles,
} from "models/propTypes/genericFormProps";
import { FormEnums, StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { FormFieldConstants, IconDefinitionConstants, InvoiceConstants } from "utils/constants";
import { SettingsFormTypes } from "models/formTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { InvoiceSettingsFormProps } from "models/propTypes/settingsPropTypes";
import { InvoiceUnit } from "models/formTypes/settings";
//#endregion
//#region components
import { GenericForm } from "components/common";
import { InvoiceLayoutSelector } from "components/invoice";
import { useInvoiceService } from "serviceHooks";
//#endregion
import { EnumHelper, LocalizationHelperInstance } from "utils/helpers";

const InvoiceSettingsForm: ForwardRefRenderFunction<FormHandles, InvoiceSettingsFormProps> = (props, ref) => {
  const invoiceService = useInvoiceService();
  const {
    onBICOptionSelect,
    onPaymentOptionSetAsDefault,
    onPaymentOptionDelete,
    onPaymentOptionMethodChange,
    fromMailCustomMessageHandler,
    isAutomaticRemindersFeatureEnabled,
  } = props;

  return (
    <GenericForm<SettingsFormTypes.CustomerInvoiceSettings>
      formRef={ref}
      inline
      {...props}
      fields={[
        {
          label: TranslationKeys.Settings.Payment,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          icon: IconDefinitionConstants.WTransactions,
          innerFields: {
            defaultPaymentTerms: {
              label: TranslationKeys.Settings.DefaultPaymentTerms,
              descriptionText: TranslationKeys.Settings.DefaultPaymentTermsDescription,
              type: FormEnums.FormFieldType.AUTOCOMPLETE,
              extraProps: {
                isNumericString: true,
                allowNegative: false,
                valueSuffix: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Days),
                freeSolo: true,
                items: InvoiceConstants.PaymentTerms.map((item) => ({
                  id: item,
                  value: item.toString(),
                  group: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
                })),
              } as AutoCompleteExtraProps<SettingsFormTypes.CustomerInvoiceSettings>,
            },
            invoicePaymentOptions: {
              label: TranslationKeys.Settings.PaymentMethod,
              descriptionText: TranslationKeys.Settings.PaymentMethodDescription,
              type: FormEnums.FormFieldType.EDITABLETABLE,
              // MessageBubble is a MUI Tooltip in a popper, so placement is the only real
              // lever - CSS on the wrapper moves the anchor, not the bubble. Tried so far:
              //   "right"        inline default - lands on the row's "..." button
              //   "top"          <- here. Centred over the table, floats into the field above
              //   "bottom-start" under the table, but reads as pointing at the wrong row
              // Full set in MessagePlacement (messagePropTypes.ts): top/bottom/left/right,
              // each with an optional -start (align to anchor's leading edge) or -end.
              messagePlacement: "top",
              innerFields: {
                methodName: {
                  label: "",
                  type: FormEnums.FormFieldType.AUTOCOMPLETE,
                  onChangeHandler: onPaymentOptionMethodChange,
                  extraProps: {
                    referenceFieldName: FormFieldConstants.Settings.PaymentOption.MethodId,
                    items: EnumHelper.getNumericItemValues(WintSuperkollAPIModel.InvoicePaymentMethod).map((item) => ({
                      id: item,
                      value: LocalizationHelperInstance.getFormattedMessage(`Invoice.PaymentMethods_${item}`),
                    })),
                  } as AutoCompleteExtraProps<SettingsFormTypes.PaymentOption>,
                },
                number: {
                  label: "",
                  type: FormEnums.FormFieldType.TEXT,
                  overrideHideErrorBubble: true,
                },
                isDefault: {
                  label: "",
                  type: FormEnums.FormFieldType.DEFAULTTAG,
                },
              } as FormFieldDefinitionType<SettingsFormTypes.PaymentOption>,
              extraProps: {
                defaultRow: { methodName: "", number: undefined, isDefault: false },
                isCompactTable: true,
                idFieldKey: FormFieldConstants.Settings.PaymentOption.Id,
                moreActions: (row: SettingsFormTypes.PaymentOption) => [
                  {
                    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.MarkAsDefault),
                    icon: IconDefinitionConstants.WCheck,
                    onClickHandler: onPaymentOptionSetAsDefault,
                    // Already the default - the green tag in the row says so.
                    hidden: row.isDefault,
                  },
                  {
                    description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Delete),
                    icon: IconDefinitionConstants.WTrashAlt,
                    onClickHandler: onPaymentOptionDelete,
                    highlightType: StyleEnums.DisplayType.Danger,
                  },
                ],
              } as EditableTableFieldProps<SettingsFormTypes.PaymentOption>,
            },
            invoiceIBAN: {
              label: TranslationKeys.Settings.IBAN,
              descriptionText: TranslationKeys.Settings.IBANDescription,
              type: FormEnums.FormFieldType.TEXT,
            },
            invoiceBIC: {
              label: TranslationKeys.Settings.BIC,
              descriptionText: TranslationKeys.Settings.BICDescription,
              type: FormEnums.FormFieldType.AUTOCOMPLETE,
              extraProps: {
                freeSolo: true,
                items: InvoiceConstants.BICOptions.map((item) => ({
                  id: item.value,
                  value: item.item.toString(),
                })),
                onItemSelect: onBICOptionSelect,
              } as AutoCompleteExtraProps<SettingsFormTypes.CustomerInvoiceSettings>,
            },
          },
        },
        {
          label: TranslationKeys.Settings.InvoiceManagement,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          icon: IconDefinitionConstants.WPaperPlane,
          innerFields: {
            invoiceSendTypeName: {
              label: TranslationKeys.Settings.InvoiceDeliveryMethod,
              descriptionText: TranslationKeys.Settings.InvoiceDeliveryMethodDescription,
              type: FormEnums.FormFieldType.AUTOCOMPLETE,
              extraProps: {
                referenceFieldName: FormFieldConstants.Settings.CustomerInvoiceSettings.InvoiceSendType,
                items: EnumHelper.getNumericItemValues(WintSuperkollAPIModel.InvoiceSendMethod).map((item) => ({
                  id: item,
                  value: invoiceService.getSendAsTypeText(item),
                })),
              },
            },
          },
        },
        {
          label: TranslationKeys.Settings.InvoiceMail,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          icon: IconDefinitionConstants.WAt,
          innerFields: {
            sendInvoiceFromName: {
              label: TranslationKeys.Settings.SendInvoicesFrom,
              descriptionText: TranslationKeys.Settings.SendInvoicesFromDescription,
              type: FormEnums.FormFieldType.AUTOCOMPLETE,
              customMessageHandler: fromMailCustomMessageHandler,
              extraProps: {
                referenceFieldName: FormFieldConstants.Settings.CustomerInvoiceSettings.SendInvoiceFromId,
                items: EnumHelper.getNumericItemValues(WintSuperkollAPIModel.SendInvoiceMailsFromEnum).map((item) => ({
                  id: item,
                  value: LocalizationHelperInstance.getFormattedMessage(`Settings.SendInvoiceFrom_${item}`),
                })),
              } as AutoCompleteExtraProps<SettingsFormTypes.CustomerInvoiceSettings>,
            },
            companyEmail: {
              label: TranslationKeys.Settings.InvoiceEmailForCompany,
              descriptionText: TranslationKeys.Settings.InvoiceEmailForCompanyDescription,
              type: FormEnums.FormFieldType.TEXT,
            },
            invoiceTitleTemplate: {
              label: TranslationKeys.Settings.InvoiceMail,
              descriptionText: TranslationKeys.Settings.InvoiceMailDescription,
              type: FormEnums.FormFieldType.HORIONTALFIELDGROUP,
              innerFields: {
                invoiceTitleTemplate: {
                  label: TranslationKeys.Common.Subject,
                  type: FormEnums.FormFieldType.TEXT,
                  widthColumnCount: 12,
                },
                invoiceMessageTemplate: {
                  label: TranslationKeys.Common.Message,
                  type: FormEnums.FormFieldType.TEXTAREA,
                  widthColumnCount: 12,
                },
              },
            },
            invoiceFileNameTemplate: {
              label: TranslationKeys.Settings.FileNameTemplate,
              descriptionText: TranslationKeys.Settings.FileNameTemplateDescription,
              type: FormEnums.FormFieldType.TEXT,
            },
          },
        },
        {
          label: TranslationKeys.Settings.Reminders,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          icon: IconDefinitionConstants.WBell,
          innerFields: {
            reminderCost: {
              label: TranslationKeys.Settings.ReminderCost,
              descriptionText: TranslationKeys.Settings.ReminderCostDescription,
              type: FormEnums.FormFieldType.NUMBER,
            },
            penaltyInterestType: {
              label: TranslationKeys.Settings.PenaltyInterestType,
              type: FormEnums.FormFieldType.CHOICE,
              descriptionText: TranslationKeys.Settings.PenaltyInterestTypeDescription,
              extraProps: {
                items: EnumHelper.getNumericItemValues(WintSuperkollAPIModel.InvoicePenaltyInterestType).map(
                  (state) => ({
                    id: state,
                    value: LocalizationHelperInstance.getFormattedMessage(`Settings.PenaltyInterestType_${state}`),
                  })
                ),
              },
            },
            penaltyInterestPercentage: {
              label: TranslationKeys.Settings.InterestRate,
              descriptionText: TranslationKeys.Settings.InterestRateDescription,
              type: FormEnums.FormFieldType.NUMBER,
              extraProps: {
                decimalPoints: 2,
                suffix: "%",
                max: 100,
              },
            },
            isAutomaticRemindersEnabled: {
              label: TranslationKeys.Invoice.ReminderManagement,
              descriptionText: TranslationKeys.Invoice.ReminderManagementDescription,
              type: FormEnums.FormFieldType.CHOICE,
              extraProps: {
                items: [
                  {
                    id: 0,
                    value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ManualReminder),
                  },
                  {
                    id: 1,
                    value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.AutoReminder),
                  },
                ],
              },
              hidden: !isAutomaticRemindersFeatureEnabled,
            },
            automaticReminderAmountLimit: {
              label: TranslationKeys.Settings.AutomaticReminderAmountLimit,
              descriptionText: TranslationKeys.Settings.AutomaticReminderAmountLimitDescription,
              controlLabel: TranslationKeys.Settings.AutomaticReminderAmountLimitControlLabel,
              type: FormEnums.FormFieldType.CURRENCY,
              hidden: !isAutomaticRemindersFeatureEnabled,
            },
            automaticReminderDelay: {
              label: TranslationKeys.Settings.AutomaticReminderDelay,
              descriptionText: TranslationKeys.Settings.AutomaticReminderDelayDescription,
              controlLabel: TranslationKeys.Settings.AutomaticReminderDelayControlLabel,
              type: FormEnums.FormFieldType.NUMBER,
              hidden: !isAutomaticRemindersFeatureEnabled,
            },
            automaticReminderPeriod: {
              label: TranslationKeys.Settings.AutomaticReminderPeriod,
              descriptionText: TranslationKeys.Settings.AutomaticReminderPeriodDescription,
              controlLabel: TranslationKeys.Settings.AutomaticReminderPeriodControlLabel,
              type: FormEnums.FormFieldType.NUMBER,
              hidden: !isAutomaticRemindersFeatureEnabled,
            },
            automaticReminderCountLimit: {
              label: TranslationKeys.Settings.AutomaticReminderCountLimit,
              descriptionText: TranslationKeys.Settings.AutomaticReminderCountLimitDescription,
              controlLabel: TranslationKeys.Settings.AutomaticReminderCountLimitControlLabel,
              type: FormEnums.FormFieldType.NUMBER,
              hidden: !isAutomaticRemindersFeatureEnabled,
            },
            invoiceReminderNotesTemplate: {
              label: TranslationKeys.Settings.InvoiceReminderNotesTemplate,
              descriptionText: TranslationKeys.Settings.InvoiceReminderNotesTemplateDescription,
              type: FormEnums.FormFieldType.TEXTAREA,
            },
            invoiceReminderSendTypeName: {
              label: TranslationKeys.Settings.InvoiceReminderSendType,
              descriptionText: TranslationKeys.Settings.InvoiceReminderSendTypeDescription,
              type: FormEnums.FormFieldType.AUTOCOMPLETE,
              extraProps: {
                referenceFieldName: FormFieldConstants.Settings.CustomerInvoiceSettings.InvoiceReminderSendType,
                items: EnumHelper.getNumericItemValues(WintSuperkollAPIModel.InvoiceSendType).map((key) => ({
                  id: key,
                  value: LocalizationHelperInstance.getFormattedMessage(`Invoice.AutomaticReminderSendType_${key}`),
                })),
              },
            },
            invoiceReminderTitleTemplate: {
              label: TranslationKeys.Settings.InvoiceReminderMail,
              descriptionText: TranslationKeys.Settings.InvoiceReminderMailDescription,
              type: FormEnums.FormFieldType.HORIONTALFIELDGROUP,
              innerFields: {
                invoiceReminderTitleTemplate: {
                  label: TranslationKeys.Common.Subject,
                  type: FormEnums.FormFieldType.TEXT,
                  widthColumnCount: 12,
                },
                invoiceReminderMessageTemplate: {
                  label: TranslationKeys.Common.Message,
                  type: FormEnums.FormFieldType.TEXTAREA,
                  widthColumnCount: 12,
                },
              },
            },
            invoiceReminderFileNameTemplate: {
              label: TranslationKeys.Settings.InvoiceReminderFileNameTemplate,
              descriptionText: TranslationKeys.Settings.InvoiceReminderFileNameTemplateDescription,
              type: FormEnums.FormFieldType.TEXT,
            },
          },
        },
        {
          label: TranslationKeys.Settings.DisplaySettings,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          icon: IconDefinitionConstants.WEye,
          innerFields: {
            defaultInvoiceLayout: {
              label: TranslationKeys.Settings.InvoicePdfTemplate,
              descriptionText: TranslationKeys.Settings.InvoicePdfTemplateDescription,
              type: FormEnums.FormFieldType.CUSTOM,
              customField: <InvoiceLayoutSelector onChange={() => {}} onBlur={() => {}} />,
            },
            defaultNotes: {
              label: TranslationKeys.Settings.InvoiceMessage,
              descriptionText: TranslationKeys.Settings.InvoiceMessageDescription,
              type: FormEnums.FormFieldType.TEXTAREA,
            },
            homePageUrl: {
              label: TranslationKeys.Settings.HomePageUrl,
              descriptionText: TranslationKeys.Settings.HomePageUrlDescription,
              type: FormEnums.FormFieldType.TEXT,
            },
            useFTax: {
              label: TranslationKeys.Settings.UseFTax,
              descriptionText: TranslationKeys.Settings.UseFTaxDescription,
              type: FormEnums.FormFieldType.TOGGLE,
            },
            hideDeliveryDate: {
              label: TranslationKeys.Settings.HideDeliveryAddress,
              descriptionText: TranslationKeys.Settings.HideDeliveryAddressDescription,
              type: FormEnums.FormFieldType.TOGGLE,
            },
          },
        },
        {
          label: TranslationKeys.Settings.Units,
          type: FormEnums.FormSectionType.FIELDGROUPSECTION,
          icon: IconDefinitionConstants.WList,
          innerFields: {
            units: {
              label: TranslationKeys.Settings.Units,
              type: FormEnums.FormFieldType.EDITABLETABLE,
              innerFields: {
                name: {
                  label: TranslationKeys.Settings.UnitName,
                  type: FormEnums.FormFieldType.TEXT,
                },
              } as FormFieldDefinitionType<InvoiceUnit>,
              extraProps: {
                defaultRow: { name: "" },
                isCompactTable: true,
              } as EditableTableFieldProps<InvoiceUnit>,
            },
          },
        },
      ]}
    />
  );
};

export default forwardRef(InvoiceSettingsForm);
